import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../auth/AuthContext'

/**
 * Active organization control.
 *
 * A user with one organization gets a plain label — a dropdown that can only ever
 * resolve to its current value is noise. A user with several gets a menu, and choosing
 * clears the query cache so no screen can keep showing the previous organization's
 * numbers under the new organization's name.
 */
export function OrgSwitcher() {
  const { organizations, activeOrg, setActiveOrg, hasNoOrganization } = useAuth()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return

    function onPointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  if (hasNoOrganization) {
    return (
      <span
        className="badge"
        style={{ background: 'var(--color-error-wash)', color: 'var(--color-error)' }}
        title="Your account is not a member of any organization. Ask an administrator to add you to one."
      >
        No organization
      </span>
    )
  }

  if (organizations.length === 0) {
    return null
  }

  if (organizations.length === 1) {
    return (
      <span className="flex items-baseline gap-1.5">
        <span className="col-head">org</span>
        <span className="text-[12.5px] font-medium">{organizations[0]!.name}</span>
      </span>
    )
  }

  function choose(id: string) {
    setOpen(false)
    if (id === activeOrg?.id) return

    setActiveOrg(id)
    // Everything fetched so far belongs to the previous organization.
    void queryClient.clear()
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        className="btn btn-sm"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={
          activeOrg
            ? undefined
            : { borderColor: 'var(--color-accent)', color: 'var(--color-accent)' }
        }
      >
        <span className="col-head">org</span>
        <span className="font-medium">{activeOrg?.name ?? 'Select organization'}</span>
        <span aria-hidden style={{ color: 'var(--color-faint)' }}>
          ▾
        </span>
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute right-0 z-50 mt-1 min-w-56 overflow-hidden rounded border py-1"
          style={{
            background: 'var(--color-panel)',
            // A rule rather than a shadow: v4 drops the shadow tokens and forbids
            // shadows outright. Separation comes from the border and the fill.
            borderColor: 'var(--color-ink)',
          }}
        >
          {organizations.map((org) => {
            const active = org.id === activeOrg?.id
            return (
              <li key={org.id}>
                <button
                  role="option"
                  aria-selected={active}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12.5px]"
                  style={{
                    background: active ? 'var(--color-accent-wash)' : 'transparent',
                    color: active ? 'var(--color-accent)' : 'var(--color-ink)',
                    fontWeight: active ? 600 : 400,
                  }}
                  onClick={() => choose(org.id)}
                >
                  <span className="flex-1">{org.name}</span>
                  <span className="ident" style={{ color: 'var(--color-faint)' }}>
                    {org.id}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
